import { useEffect, useRef, useState } from 'react';
import { signOut } from 'aws-amplify/auth';
import { NavLink, useNavigate } from 'react-router-dom';
import { ROUTES } from '../routes.ts';
import { useUserName, useIsDoctor, useIsOperator, useOrgAdminOf } from '../auth.ts';
import { ChevronDownIcon, HamburgerIcon, MenuCloseIcon } from './icons.tsx';
import styles from './AppHeader.module.css';
import { getInitialsFromName } from '@/utils';
import type { PublicOrganization } from '@dermalink/shared';
import logoIcon from '../assets/logo-icon-color.svg';

interface AppHeaderProps {
  hideNav?: boolean;
  /** Whitelabeled practice page: the practice's logo and/or name lead, Dermalink is credited as the platform. */
  practice?: PublicOrganization | null;
}

export function AppHeader({ hideNav = false, practice }: AppHeaderProps) {
  const navigate = useNavigate();
  const isDoctor = useIsDoctor();
  const isOperator = useIsOperator();
  const isOrgAdmin = useOrgAdminOf().length > 0;
  const userName = useUserName();
  const homeRoute = '/';
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLElement>(null);
  const normalizedUserName = userName.trim();
  if (!normalizedUserName) {
    throw new Error('AppHeader requires a non-empty userName');
  }
  const userInitials = getInitialsFromName(normalizedUserName);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setMobileMenuOpen(false);
      }
    }
    if (menuOpen || mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, mobileMenuOpen]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 640) setMobileMenuOpen(false);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  async function handleSignOut() {
    await signOut();
    navigate(ROUTES.login, { replace: true });
  }

  return (
    <header className={styles.header} ref={menuRef}>
      <NavLink to={homeRoute} className={styles.logo}>
        {practice ? <PracticeMark practice={practice} /> : (
          <>
            <img src={logoIcon} alt="" aria-hidden="true" className={styles.logoIcon} />
            <span className={styles.logoText}>Dermalink</span>
          </>
        )}
      </NavLink>

      <div className={styles.right}>
        {!hideNav && !isDoctor && (
          <NavLink to={ROUTES.practices} className={styles.dropdownTrigger}>Praktijken</NavLink>
        )}
        {!hideNav && isOrgAdmin && (
          <NavLink to={ROUTES.practiceAdmin} className={styles.dropdownTrigger}>Praktijk</NavLink>
        )}
        {!hideNav && isOperator && (
          <NavLink to={ROUTES.operator} className={styles.dropdownTrigger}>Beheerder</NavLink>
        )}

        <div className={styles.userMenu}>
          <button
            type="button"
            className={styles.userTrigger}
            onClick={() => setMenuOpen(v => !v)}
            aria-expanded={menuOpen}
            aria-haspopup="true"
          >
            <span className={styles.userInitials} aria-hidden="true">{userInitials}</span>
            <span className={styles.userName}>{userName || '…'}</span>
            <ChevronDownIcon className={styles.chevron} />
          </button>

          {menuOpen && (
            <div className={styles.dropdown}>
              <NavLink to={ROUTES.profile} className={styles.dropdownItem} onClick={() => setMenuOpen(false)}>Profiel</NavLink>
              <button
                type="button"
                className={`${styles.dropdownItem} ${styles.dropdownItemDestructive}`}
                onClick={handleSignOut}
              >
                Afmelden
              </button>
            </div>
          )}
        </div>
      </div>

      {!hideNav && (
        <button
          type="button"
          className={styles.hamburger}
          onClick={() => setMobileMenuOpen(v => !v)}
          aria-expanded={mobileMenuOpen}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <MenuCloseIcon /> : <HamburgerIcon />}
        </button>
      )}

      {mobileMenuOpen && !hideNav && (
        <div className={styles.mobileDrawer}>
          {!isDoctor && <NavLink to={ROUTES.practices} end className={styles.mobileDrawerItem} onClick={() => setMobileMenuOpen(false)}>Praktijken</NavLink>}
          <NavLink to={ROUTES.profile} className={styles.mobileDrawerItem} onClick={() => setMobileMenuOpen(false)}>Profiel</NavLink>
          {isOrgAdmin && (
            <>
              <NavLink to={ROUTES.practiceAdminReport} className={styles.mobileDrawerItem} onClick={() => setMobileMenuOpen(false)}>Praktijk: overzicht</NavLink>
              <NavLink to={ROUTES.practiceAdminBranding} className={styles.mobileDrawerItem} onClick={() => setMobileMenuOpen(false)}>Praktijk: huisstijl</NavLink>
              <NavLink to={ROUTES.practiceAdminPayments} className={styles.mobileDrawerItem} onClick={() => setMobileMenuOpen(false)}>Praktijk: betalingen</NavLink>
            </>
          )}
          {isOperator && (
            <NavLink to={ROUTES.operator} className={styles.mobileDrawerItem} onClick={() => setMobileMenuOpen(false)}>Beheerder</NavLink>
          )}
          <button
            type="button"
            className={`${styles.mobileDrawerItem} ${styles.mobileDrawerItemDestructive}`}
            onClick={handleSignOut}
          >
            Afmelden
          </button>
          <div className={styles.mobileDrawerUser}>
            Ingelogd als {userName || '…'}
          </div>
        </div>
      )}
    </header>
  );
}

/** Shared with the practice settings preview, so admins see exactly what patients get. */
export function PracticeMark({ practice }: { practice: Pick<PublicOrganization, 'name' | 'logo'> }) {
  const { logo } = practice;
  return (
    <>
      {logo
        ? <img src={logo.url} width={logo.width} height={logo.height} alt="" aria-hidden="true" className={styles.logoPracticeImage} />
        : <img src={logoIcon} alt="" aria-hidden="true" className={styles.logoIcon} />}
      <span className={styles.logoPractice}>
        <span className={styles.logoText}>{practice.name}</span>
        <span className={styles.logoVia}>via Dermalink</span>
      </span>
    </>
  );
}
